<template>
  <div :class="['dev-panel', open ? 'dev-panel-open' : 'dev-panel-closed']">
    <div class="dev-panel-tab" @click="$emit('toggle')" />
    <div class="dev-panel-inner">
      <div class="row mb-3">
        <strong>Package Version:</strong> {{ packageVersion }}<br />
        <strong>Build Time:</strong> {{ buildTime }}
      </div>
      <div class="row mb-3">
        <div v-for="locale in locales" :key="locale" class="col">
          <button
            class="btn btn-sm"
            :class="$i18n.locale === locale ? 'btn-warning' : 'btn-light'"
            @click="$emit('change-locale', locale)"
          >
            {{ locale }}
          </button>
        </div>
      </div>
      <div class="row mb-3">
        <div class="col">
          <button
            class="btn btn-sm"
            :class="disableButtonDisappearance ? 'btn-warning' : 'btn-light'"
            @click="$emit('update:disableButtonDisappearance', !disableButtonDisappearance)"
          >
            Disable button disappearance ({{ disableButtonDisappearance ? 'On' : 'Off' }})
          </button>
        </div>
        <div class="col">
          <button
            class="btn btn-sm" :class="disableTransitions ? 'btn-warning' : 'btn-light'"
            @click="$emit('toggle-disable-transitions')"
          >
            Disable transitions ({{ disableTransitions ? 'On' : 'Off' }})
          </button>
        </div>
        <div class="col">
          <select v-model="transitionDurationScaleProxy" class="form-control">
            <option
              v-for="transitionDurationScaleValue in transitionDurationScales"
              :key="transitionDurationScaleValue"
              :value="transitionDurationScaleValue"
            >
              {{ transitionDurationScaleValue }} ({{ 800 * transitionDurationScaleValue }}ms)
            </option>
          </select>
        </div>
      </div>
      <div class="row mb-3">
        <div class="col">
          <button
            class="btn btn-sm" :class="autoAnswer ? 'btn-warning' : 'btn-light'"
            @click="$emit('update:autoAnswer', !autoAnswer)"
          >
            Auto answer ({{ autoAnswer ? 'On' : 'Off' }})
          </button>
        </div>
        <div class="col">
          <button
            class="btn btn-sm btn-light"
            @click="$emit('auto-answer-once')"
          >
            Auto answer once
          </button>
        </div>
        <div class="col">
          <button
            class="btn btn-sm" :class="autoContinue ? 'btn-warning' : 'btn-light'"
            @click="$emit('update:autoContinue', !autoContinue)"
          >
            Auto continue ({{ autoContinue ? 'On' : 'Off' }})
          </button>
        </div>
        <div class="col">
          <button
            class="btn btn-sm" :class="showQuestionAnswerIds ? 'btn-success' : 'btn-light'"
            @click="$emit('toggle-show-question-answer-ids')"
          >
            Show Q/A ids ({{ showQuestionAnswerIds ? 'On' : 'Off' }})
          </button>
        </div>
      </div>
      <div class="row mb-3">
        <div class="col">
          <button class="btn btn-sm btn-success" @click="$emit('prefill', 'green')">
            Prefill Green
          </button>
        </div>
        <div class="col">
          <button class="btn btn-sm btn-warning" @click="$emit('prefill', 'yellow')">
            Prefill Yellow
          </button>
        </div>
        <div class="col">
          <button class="btn btn-sm btn-danger" @click="$emit('prefill', 'red')">
            Prefill Red
          </button>
        </div>
      </div>
      <div class="row mb-3">
        <div class="col">
          <button class="btn btn-sm btn-success" @click="$emit('prefill-trigger', 'green')">
            Trigger.None
          </button>
        </div>
        <div class="col">
          <button class="btn btn-sm btn-warning" @click="$emit('prefill-trigger', 'yellow')">
            Trigger.One
          </button>
        </div>
        <div class="col">
          <button class="btn btn-sm btn-danger" @click="$emit('prefill-trigger', 'red')">
            Trigger.All
          </button>
        </div>
      </div>
      <div class="row mb-3">
        <div class="col">
          <select v-model="jumpToDestinationProxy" class="form-control question-jumper">
            <option value="">
              Select jump destination
            </option>
            <optgroup
              v-for="group in jumpDestinations"
              :key="group.index"
              :label="group.label"
            >
              <option
                v-for="question in group.questions"
                :key="question.index"
                :value="question.value"
              >
                {{ question.label }}
              </option>
            </optgroup>
          </select>
        </div>
        <div class="col">
          <button
            class="btn btn-sm btn-primary" :disabled="!jumpToDestination || progressingToJumpDestination"
            @click="$emit('jump')"
          >
            Jump to question
          </button>
        </div>
        <div class="col">
          <button
            class="btn btn-sm btn-primary" :disabled="!jumpToDestination || progressingToJumpDestination"
            @click="$emit('progress-to-jump')"
          >
            {{
              progressingToJumpDestination ? 'Progressing to question..' : 'Progress to question'
            }}
          </button>
        </div>
      </div>
      <div class="row mb-3">
        <div class="col">
          <button
            class="btn btn-sm" :class="animatingTo ? 'btn-warning' : 'btn-light'"
            @click="$emit('animate-white')"
          >
            {{ animatingTo ? `Animating to ${animatingTo}..` : 'Animate to white' }}
          </button>
        </div>
        <div class="col">
          <button
            class="btn btn-sm" :class="animatingTo ? 'btn-warning' : 'btn-light'"
            @click="$emit('animate-blue')"
          >
            {{ animatingTo ? `Animating to ${animatingTo}..` : 'Animate to blue' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * SurveyDevPanel
 *
 * Presentational dev/debug overlay for the Survey view. Holds no survey logic:
 * display values come in as props, the panel's toggle flags are two-way bound
 * with `v-model:*` (the parent survey flow reads them), and every action is
 * emitted for the parent to handle.
 */
export default {
  name: 'SurveyDevPanel',
  props: {
    open: { type: Boolean, default: false },
    packageVersion: { type: String, default: '' },
    buildTime: { type: String, default: '' },
    locales: { type: Array, default: () => [] },
    transitionDurationScales: { type: Array, default: () => [] },
    transitionDurationScale: { type: Number, default: 1 },
    disableButtonDisappearance: { type: Boolean, default: false },
    disableTransitions: { type: Boolean, default: false },
    autoAnswer: { type: Boolean, default: false },
    autoContinue: { type: Boolean, default: false },
    showQuestionAnswerIds: { type: Boolean, default: false },
    jumpDestinations: { type: Array, default: () => [] },
    jumpToDestination: { type: String, default: '' },
    progressingToJumpDestination: { type: Boolean, default: false },
    animatingTo: { type: String, default: null },
  },
  emits: [
    'toggle',
    'change-locale',
    'toggle-disable-transitions',
    'auto-answer-once',
    'toggle-show-question-answer-ids',
    'prefill',
    'prefill-trigger',
    'jump',
    'progress-to-jump',
    'animate-white',
    'animate-blue',
    'update:disableButtonDisappearance',
    'update:autoAnswer',
    'update:autoContinue',
    'update:transitionDurationScale',
    'update:jumpToDestination',
  ],
  computed: {
    // get/set proxies so the native <select> v-model preserves option value
    // types (e.g. numeric duration scales) while staying a controlled prop.
    transitionDurationScaleProxy: {
      get() {
        return this.transitionDurationScale;
      },
      set(value) {
        this.$emit('update:transitionDurationScale', value);
      },
    },
    jumpToDestinationProxy: {
      get() {
        return this.jumpToDestination;
      },
      set(value) {
        this.$emit('update:jumpToDestination', value);
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.dev-panel {
  position: fixed;
  top: 30px;
  right: 0;
  width: 10px;
  height: 300px;
  transition-property: width;
  transition-duration: 200ms;
  transition-timing-function: ease-out;

  .dev-panel-tab {
    position: absolute;
    top: 0;
    left: 0;
    width: 20px;
    background: rgba(0, 0, 255, 50%);
    height: 100%;
    z-index: 100000000;
    transition-property: background-color;
    transition-duration: 200ms;
    transition-timing-function: ease-out;

    &:hover {
      background: rgba(190, 190, 255, 50%);
    }
  }

  &.dev-panel-open {
    .dev-panel-inner {
      overflow-y: scroll;
    }
  }

  .dev-panel-inner {
    overflow: hidden;
    position: relative;
    height: 100%;
    padding-left: 40px;
    max-height: 100%;
  }

  .question-jumper {
    font-family: Consolas, Monaco, "Andale Mono", monospace;
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;

    li {
      margin: 0;
      padding: 0;
    }
  }

  &:hover {
    width: 20px;
    cursor: pointer;
  }

  &.dev-panel-open {
    width: 80vw;
    padding: 2px;

    &:hover {
      width: 80%;
    }
  }

  background: rgba(0, 0, 0, 80%);
  font-size: 10px;
  color: white;
  z-index: 999999999999;

  .col {
    text-align: center;
    border-left: 1px solid rgba(255, 255, 255, 50%);
    border-right: 1px solid rgba(255, 255, 255, 50%);

    &:first-child {
      border-left: 0;
    }

    &:last-child {
      border-right: 0;
    }

    select, input, textarea {
      text-align: left !important;
    }
  }
}
</style>
